/**
 * Lightweight, non-intrusive device fingerprint for fraud limits (the notice pop-up's
 * Generate Code button). No permission prompts and nothing sent anywhere but our own API:
 * it hashes stable browser traits plus a canvas render, alongside a random device id kept
 * in localStorage. Either one matching counts as the same device on the server, so clearing
 * storage or switching browser alone doesn't reset the limit.
 */

const DEVICE_ID_KEY = 'bloom_device_id';

function randomId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id || !/^[\w-]{8,64}$/.test(id)) {
      id = randomId();
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return ''; // storage blocked (private mode) — the fingerprint alone identifies the device
  }
}

function canvasTrait() {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 220;
    canvas.height = 40;
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = "16px 'Arial'";
    ctx.fillStyle = '#f60';
    ctx.fillRect(100, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('Bloom fp \u{1F964} 0123', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Bloom fp \u{1F964} 0123', 4, 17);
    return canvas.toDataURL();
  } catch {
    return '';
  }
}

// FNV-1a, used twice with different seeds, when Web Crypto isn't available (non-HTTPS)
function fallbackHash(text) {
  const run = (seed) => {
    let h = seed >>> 0;
    for (let i = 0; i < text.length; i += 1) {
      h ^= text.charCodeAt(i);
      h = Math.imul(h, 16777619) >>> 0;
    }
    return h.toString(16).padStart(8, '0');
  };
  return `${run(2166136261)}${run(3735928559)}${run(19088743)}${run(2309737967)}`;
}

let cached = null;

export async function getFingerprint() {
  if (cached) return cached;
  const nav = window.navigator;
  const traits = [
    nav.userAgent,
    nav.language,
    (nav.languages || []).join(','),
    nav.platform,
    nav.hardwareConcurrency,
    nav.deviceMemory,
    nav.maxTouchPoints,
    window.screen?.width,
    window.screen?.height,
    window.screen?.colorDepth,
    window.devicePixelRatio,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    new Date().getTimezoneOffset(),
    canvasTrait()
  ].join('|');

  try {
    const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(traits));
    cached = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    cached = fallbackHash(traits);
  }
  return cached;
}
